/* eslint-disable no-shadow */

/**
 * If you would like to add a new channel, you must add it to the allowed Channel Names on the API key.
 * You can find that here: https://ably.com/accounts/26691/apps/54336/app_keys
 * We block all channels that are not specified here.
 */
export enum ChannelTypeNames {
    // Chat messages
    newMessage = 'newMessage',
    newInternalChatMessage = 'newInternalChatMessage',
    // Form submissions
    formCompleted = 'formCompleted',
    // Service updates
    updateService = 'Update',
    // Notifications
    teamChatNotification = 'teamChatNotification',
    chatNotification = 'chatNotification',
    actionItemNotification = 'actionItemNotification',
    userNotification = 'userNotification',
}

export type ChannelName = `${ChannelTypeNames}-${string}`

export function constructChannel(
    channelName: ChannelTypeNames,
    id: string,
): ChannelName {
    return `${channelName}-${id}`
}

/**
 * Unwraps the standard Ably envelope. The server publishes raw payloads via
 * `publish(channel, payload)`, and `useChannel` hands us the full Ably
 * message - so the payload always lives on `.data`. Returns null for anything
 * unexpected so callers can bail to generic copy.
 */
export function getAblyMessageData(message: unknown): unknown {
    if (typeof message !== 'object' || message === null) return null
    if (!('data' in message)) return null
    return message.data
}

/**
 * Reads the chat id from a `newMessage-{clinicId}` event, whose payload is the
 * created message. Messages created through GraphQL carry the selected
 * `chat { id }` object; messages created server-side (inbound SMS, reminders,
 * bulk sends) carry the raw row, where `chat` is the id string. Returns null
 * when neither shape is present.
 */
export function getChatIdFromNewMessageEvent(message: unknown): string | null {
    const data = getAblyMessageData(message)
    if (typeof data !== 'object' || data === null || !('chat' in data)) {
        return null
    }

    const { chat } = data
    if (typeof chat === 'string') return chat || null
    if (typeof chat !== 'object' || chat === null || !('id' in chat)) {
        return null
    }
    return typeof chat.id === 'string' && chat.id ? chat.id : null
}

// ---------------------------------------------------------------------------
// Chat presence (typing / viewing indicators)
// ---------------------------------------------------------------------------

/**
 * What a clinic user is doing inside an open conversation. Published as Ably
 * presence data on the conversation's existing `newMessage-{chatId}` channel,
 * so no new channel allowlist entries and no DB table are required.
 *
 * NOTE: the `/ably/auth` token must grant the `presence` capability on
 * `newMessage-*` for this to work (server-side change).
 */
export enum ChatPresenceState {
    Viewing = 'viewing',
    Typing = 'typing',
}

export interface ChatPresenceData {
    userId: string
    firstName: string
    lastName?: string | null
    avatarUrl?: string | null
    state: ChatPresenceState
}

/**
 * Validates untrusted presence payloads from other clients. Returns null for
 * anything malformed (including members that entered before auth resolved)
 * so the indicator never renders garbage.
 */
export function parseChatPresenceData(data: unknown): ChatPresenceData | null {
    if (typeof data !== 'object' || data === null) return null
    if (!('userId' in data) || !('firstName' in data) || !('state' in data)) {
        return null
    }

    const { userId, firstName, state } = data
    if (typeof userId !== 'string' || !userId) return null
    if (typeof firstName !== 'string' || !firstName) return null
    if (
        state !== ChatPresenceState.Viewing &&
        state !== ChatPresenceState.Typing
    ) {
        return null
    }

    const lastName = 'lastName' in data ? data.lastName : null
    if (
        lastName !== null &&
        lastName !== undefined &&
        typeof lastName !== 'string'
    ) {
        return null
    }

    const avatarUrl = 'avatarUrl' in data ? data.avatarUrl : null
    if (
        avatarUrl !== null &&
        avatarUrl !== undefined &&
        typeof avatarUrl !== 'string'
    ) {
        return null
    }

    return {
        userId,
        firstName,
        lastName: lastName ?? null,
        avatarUrl: avatarUrl ?? null,
        state,
    }
}
